/**
 * IELTS question forms (D-105), shared by the web and the mobile app: one shape for a course
 * exercise (`exercise_view`: prompt + data) and an exam question (`stem` + options + data), the
 * learner's response, what « nothing given yet » means, and the correction (Feedback).
 * The server stays the reference (apps/academy/ielts.py `check`, course.py `exercise_view`).
 */

import { countWords } from "../tcf";
import { type ExamPicture, parsePicture, parsePictures } from "../pictures";
import { isObject, num, numOrNull, numbers, str, strings } from "./read";

export type ItemType = "mcq" | "multi" | "gap" | "tfng" | "match" | "order" | "short" | "write" | "speak";
export const ITEM_TYPES: readonly ItemType[] = ["mcq", "multi", "gap", "tfng", "match", "order", "short", "write", "speak"];
/** Marked at once by the server (course.py AUTO); « write » and « speak » are marked by the AI. */
export const AUTO_TYPES: readonly ItemType[] = ["mcq", "multi", "gap", "tfng", "match", "order", "short"];
export const TFNG = ["TRUE", "FALSE", "NOT GIVEN"] as const;

/** What the learner gives: an index, a list (indexes or words), a word or a text, or nothing. */
export type ItemResponse = number | string | (number | string | null)[] | null;

/** One exercise of the course or one question of a sitting, in a common form. */
export interface Item {
  id: string;
  type: ItemType;
  prompt: string;
  options: string[];
  left: string[];
  right: string[];
  items: string[];
  /** « multi »: how many letters to choose. */
  choose: number;
  maxWords: number | null;
  /** « NO MORE THAN TWO WORDS AND/OR A NUMBER »: one number allowed on top of the words (D-136). */
  andNumber: boolean;
  minWords: number | null;
  seconds: number | null;
  /** Text to read (a notice, an e-mail…). */
  text: string;
  /** Listening script: read aloud (en-GB), never shown before the answer. */
  script: string;
  instructions: string;
  /** The scene of a listening (TCF, D-107): shown above the player, never read aloud. */
  context: string;
  /** D-118: how a German true / false item is labelled (« de », « ja_nein »), "" otherwise. */
  tfngLabels: string;
  /** D-118: the instruction of an A1 / A2 German exercise in French and English. */
  promptHelp: { fr: string; en: string } | null;
  /** Exam numbering: first number and every number covered (« Questions 11–15 »). */
  number: number | null;
  numbers: number[];
  marks: number;
  /** The picture of the question (signed link, never kept across sittings), or null. */
  image: ExamPicture | null;
  /** One picture per option (TEF CO section 1: « Dessin A » … « Dessin D »), null where none. */
  optionImages: (ExamPicture | null)[];
}

export function isItemType(value: unknown): value is ItemType {
  return typeof value === "string" && (ITEM_TYPES as readonly string[]).includes(value);
}

/** A blank of a gap sentence: three underscores or more (« ___ »). */
const GAP = /_{3,}/g;

/** Number of blanks of a gap prompt (« ___ »), at least one. */
export function gapCount(prompt: string): number {
  return Math.max(1, (prompt.match(GAP) ?? []).length);
}

/** The pieces of text around the blanks (« I ___ a student » → ["I ", " a student"]). */
export function splitGaps(prompt: string): string[] {
  return prompt.split(GAP);
}

/** The question carries its own blanks (a gap sentence): no separate prompt above the answer. */
export function hasOwnPrompt(item: Pick<Item, "type" | "prompt">): boolean {
  return item.type === "gap" && splitGaps(item.prompt).length > 1;
}

/** A course exercise (`exercise_view`: prompt + data) or an exam question (`stem` + options + data). */
export function parseItem(value: unknown): Item | null {
  if (!isObject(value)) return null;
  const data = isObject(value.data) ? value.data : {};
  const type = isItemType(value.type) ? value.type : "mcq";
  const options = strings(Array.isArray(value.options) && value.options.length ? value.options : data.options);
  return {
    id: str(value.id),
    type,
    prompt: str(value.prompt) || str(value.stem),
    options,
    left: strings(data.left),
    right: strings(data.right),
    items: strings(data.items),
    // the IELTS « Choose TWO letters » when the exercise does not say
    choose: num(data.choose, type === "multi" ? 2 : 1),
    maxWords: numOrNull(data.max_words),
    andNumber: data.and_number === true,
    minWords: numOrNull(data.min_words),
    seconds: numOrNull(data.seconds),
    text: str(data.text),
    script: str(data.script),
    instructions: str(data.instructions),
    context: str(data.context),
    tfngLabels: str(data.tfng_labels),
    promptHelp: isObject(data.prompt_help) ? { fr: str(data.prompt_help.fr), en: str(data.prompt_help.en) } : null,
    number: numOrNull(value.number),
    numbers: numbers(data.numbers),
    marks: num(value.marks, 1),
    image: parsePicture(value.image),
    optionImages: parsePictures(value.option_images),
  };
}

const NUMBER_WORD = /^[£$€]?\d[\d.,:/%]*(?:am|pm|st|nd|rd|th)?$/i;

/**
 * Over the word limit of the instruction (the server decides; this only warns while typing):
 * every word counts, and with « … AND/OR A NUMBER » one number is allowed on top of the words
 * (« 3 large boxes », « 14 March »), a number written in groups (« 613 555 0147 ») being one.
 */
export function overWordLimit(text: string, maxWords: number | null, andNumber = false): boolean {
  if (maxWords === null) return false;
  if (!andNumber) return countWords(text) > maxWords;
  let words = 0;
  let numbers = 0;
  let previous = false;
  for (const token of text.replace(/\u00a0/g, " ").split(/\s+/).filter((w) => /[\p{L}\p{N}]/u.test(w))) {
    const isNumber = NUMBER_WORD.test(token);
    if (isNumber && !previous) numbers += 1;
    else if (!isNumber) words += 1;
    previous = isNumber;
  }
  return words + Math.max(numbers - 1, 0) > maxWords;
}

/** A response read from the server (or null). */
export function parseResponse(value: unknown): ItemResponse {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map((v) => (typeof v === "number" || typeof v === "string" ? v : null));
  return null;
}

/** Nothing given yet (every blank empty, no choice): the « Check » button stays off. */
export function isEmptyResponse(item: Pick<Item, "type">, value: ItemResponse | undefined): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return !value.trim();
  if (typeof value === "number") return false;
  if (item.type === "order") return false; // the given order is already an answer
  if (item.type === "multi") return value.length === 0;
  return value.every((v) => v === null || (typeof v === "string" && !v.trim()));
}

/**
 * The first response shown for an item: the one already given (server or phone) when there is
 * one, else nothing chosen (orders start in the given order, one empty field per gap).
 */
export function initialResponse(item: Item, given?: unknown): ItemResponse {
  const kept = parseResponse(given);
  if (kept !== null) return kept;
  switch (item.type) {
    case "order":
      return item.items.map((_, i) => i);
    case "gap":
      return Array.from({ length: gapCount(item.prompt) }, () => "");
    case "match":
      return item.left.map(() => null);
    case "multi":
      return [];
    case "short":
    case "write":
      return "";
    default:
      return null;
  }
}

/** Words as IELTS counts them (« part-time » is one word): same rule as the server's word_count. */
export function wordCount(text: string): number {
  return countWords(text);
}

/** The first accepted answer of a text answer (a gap may accept several spellings). */
export function firstAccepted(value: unknown): string {
  if (Array.isArray(value)) return firstAccepted(value[0]);
  return value === null || value === undefined ? "" : String(value);
}

/** 0 → « A », 1 → « B »… */
export function letter(index: number): string {
  return String.fromCharCode(65 + index);
}

/** The expected answer as a readable text (« B », « TRUE », « am / is », « 1 → C »…). */
export function answerText(item: Item, answer: unknown): string {
  const mark = (i: unknown) => (typeof i === "number" ? letter(i) : str(i));
  const list = Array.isArray(answer) ? answer : [answer];
  switch (item.type) {
    case "mcq":
      return typeof answer === "number" ? `${mark(answer)} · ${item.options[answer] ?? ""}` : str(answer);
    case "multi":
      return list.map((i) => (typeof i === "number" ? `${mark(i)} · ${item.options[i] ?? ""}` : str(i))).join(" ; ");
    case "gap":
      return list.map((accepted) => (Array.isArray(accepted) ? accepted.map((a) => str(a)).join(" / ") : str(accepted))).join(" · ");
    case "match":
      return list
        .map((i, index) => `${item.left[index] ?? index + 1} → ${typeof i === "number" ? (item.right[i] ?? mark(i)) : str(i)}`)
        .join(" ; ");
    case "order":
      return list.map((i) => (typeof i === "number" ? (item.items[i] ?? "") : str(i))).join(" → ");
    case "short":
      return list.map((a) => str(a)).filter(Boolean).join(" / ");
    default:
      return str(answer);
  }
}

// --- Correction ---------------------------------------------------------------------------------

/** Correction of one answer (course, review of the day, placement, training sitting). */
export interface Feedback {
  correct: boolean | null;
  points: number | null;
  marks: number | null;
  answer: unknown;
  explanation: string;
  helpFr: string;
  /** The English help of a TCF correction (D-107). */
  helpEn: string;
  modelAnswer: string;
  xpGained: number;
  nextLesson: string | null;
  status: string;
  /** « write » / « speak »: sent, marked by the AI in the background (always an estimate). */
  submitted: boolean;
  wordWarning: string;
}

/**
 * The correction from an answer's reply (course.py `answer`, `_record`) or from an exercise already
 * done (`result`: points, marks, correct, production, status), the reply winning over the result.
 */
export function parseFeedback(value: unknown): Feedback {
  const data = isObject(value) ? value : {};
  const done = isObject(data.result) ? data.result : {};
  const pick = (key: string): unknown => (data[key] !== undefined && data[key] !== null ? data[key] : done[key]);
  const correct = pick("correct");
  const status = str(pick("status"));
  return {
    correct: typeof correct === "boolean" ? correct : null,
    points: numOrNull(pick("points")),
    marks: numOrNull(pick("marks")),
    answer: data.answer ?? data.accepted ?? null,
    explanation: str(data.explanation),
    helpFr: str(data.help_fr),
    helpEn: str(data.help_en),
    modelAnswer: str(data.model_answer),
    xpGained: num(data.xp_gained),
    nextLesson: str(data.next_lesson) || null,
    status,
    submitted: Boolean(status || str(pick("production"))),
    wordWarning: str(data.word_warning),
  };
}
